import { getExtensionValue, isEmpty, MedplumClient } from "@medplum/core";
import {
  CodeableConcept,
  Coding,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from "@medplum/fhirtypes";

import { SentryInstance } from "@/bots/utils/sentry";

import { processBackgroundInformation } from "./background-information";
import { processBillingInformation } from "./billing-information";
import { processContactInformation } from "./contact-information";
import { processCurrentPregnancy } from "./current-pregnancy";
import { processDemographic } from "./demographic";
import { processFamilyMedicalHistory } from "./family-medical-history";
import { processGeneralHealth } from "./general-health";
import { processGynecologicalSexualHealth } from "./gynecological-sexual-health";
import { processIdentifyingInformation } from "./identifying-information";
import { processLifestyle } from "./lifestyle";
import { processPastPregnancies } from "./past-pregnancies";
import { processSupportNetwork } from "./support-network";

const ANSWER_TYPES = [
  "valueBoolean",
  "valueDecimal",
  "valueInteger",
  "valueDate",
  "valueDateTime",
  "valueTime",
  "valueString",
  "valueUri",
  "valueAttachment",
  "valueQuantity",
  "valueReference",
  "valueCoding",
] as const;

enum Extensions {
  QuestionnaireResponseItemIsAccepted = "http://quiltedhealth.com/sd/questionnaire-response-item-is-accepted",
}

enum YesNoOrDontKnow {
  Yes = "Yes",
  No = "No",
  Unknown = "Don't know",
}

export enum Systems {
  IdentifierType = "http://terminology.hl7.org/CodeSystem/v2-0203",
  SocialSecurity = "http://hl7.org/fhir/sid/us-ssn",
  GenderIdentity = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-genderIdentity",
  Pronouns = "http://hl7.org/fhir/StructureDefinition/individual-pronouns",
  SexualOrientation = "http://hl7.org/fhir/us/cdmh/StructureDefinition/cdmh-patient-sexualOrientation",
  SexAssignedAtBirth = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex",
  CanCallToPreferredContact = "http://quiltedhealth.com/sd/can-call-to-preferred-contact",
  CanLeaveVoicemailToPreferredContact = "http://quiltedhealth.com/sd/can-leave-voicemail-to-preferred-contact",
  MedicaidEligibility = "http://quiltedhealth.com/fhir/ValueSet/medicaid-eligibility",
  BirthPlace = "http://hl7.org/fhir/StructureDefinition/patient-birthPlace",
  GeneticParentIsCoParent = "http://quiltedhealth.com/sd/genetic-parent-is-co-parent",
  GeneticParentWillBeOnBirthCertificate = "http://quiltedhealth.com/sd/genetic-parent-will-be-on-birth-certificate",
  SupportNetworkPartnerStatusComments = "http://quiltedhealth.com/sd/support-network-partner-status-comments",
  SupportNetworkPartnerFormsetCode = "http://quiltedhealth.com/sd/support-network-partner-formset-code",
  QH = "http://quiltedhealth.com",
  UnitsOfMeasure = "http://unitsofmeasure.org",
  RelationshipRoles = "http://terminology.hl7.org/CodeSystem/v3-RoleClass",
  ConsentAction = "http://terminology.hl7.org/CodeSystem/consentaction",
  ConsentScope = "http://terminology.hl7.org/CodeSystem/consentscope",
  ConsentPolicyCodes = "http://terminology.hl7.org/CodeSystem/consentpolicycodes",
  SurgeryBloodTransfusion = "http://quiltedhealth.com/sd/surgery-blood-transfusion",
  Snomed = "http://snomed.info/sct",
  BreastSurgeryType = "http://quiltedhealth.com/fhir/CodeSystem/breast-surgery-type",
  LoincPronouns = "http://loinc.org/vs/LL5144-2",
  ResourceClientVisibility = "http://quiltedhealth.com/fhir/ValueSet/resource-client-visibility",
  ConditionCategory = "http://terminology.hl7.org/CodeSystem/condition-category",
  ProblemType = "http://quiltedhealth.com/fhir/ValueSet/problem-type",
  ConditionClinicalStatus = "http://terminology.hl7.org/CodeSystem/condition-clinical",
}

export enum LOINCCodes {
  EDDWrapper = "57060-6",
  WICAssistanceParticipant = "89088-9",
  YesNoOrDontKnow = "LL3044-6",
  HistoryOfUsualOccupation = "21843-8",
  SpiritualCultural = "81364-2",
  LevelOfEducation = "LL1069-5",
  NumberOfFetuses = "55281-0",
  OutcomeOfPregnancy = "63893-2",
  ReleaseOfPHIConsent = "64292-6",
  TotalFullTerm = "11639-2",
  TotalPregnancies = "11996-6",
  TotalAbortionsInduced = "11613-7",
  TotalPremature = "11637-6",
  TotalMiscarriage = "11614-5",
  TotalAbortionsAndMiscarriages = "11612-9",
  TotalLivingChildren = "11638-4",
  AcceptanceOfTreatment = "96395-9",
  LocationSite = "85712-8",
}

const LOINCDisplay = {
  [LOINCCodes.OutcomeOfPregnancy]: "Outcome of pregnancy",
  [LOINCCodes.TotalPregnancies]: "[#] Pregnancies",
  [LOINCCodes.TotalFullTerm]: "[#] Births.term",
  [LOINCCodes.TotalPremature]: "[#] Births.preterm",
  [LOINCCodes.TotalAbortionsInduced]: "[#] Abortions.induced",
  [LOINCCodes.TotalMiscarriage]: "[#] Abortions.spontaneous",
  [LOINCCodes.TotalAbortionsAndMiscarriages]: "[#] Abortions.total",
  [LOINCCodes.TotalLivingChildren]: "[#] Births.still living",
  [LOINCCodes.NumberOfFetuses]: "[#] Fetuses",
  [LOINCCodes.EDDWrapper]: "EDD Wrapper",
  [LOINCCodes.LocationSite]: "Expected Delivery Location Site",
} as const;

export enum TerminologyCodes {
  PatientPrivacy = "patient-privacy",
  HIPAARestrictions = "hipaa-restrictions",
  access = "access",
  ReleaseOfInformationConsentDocument = "ReleaseOfInformationConsentDocument",
  RelationshipSpouse = "SPS",
}

export enum LOINCAnswerCodes {
  Yes = "LA33-6",
  No = "LA32-8",
  Unknown = "LA4489-6",
  PronounFemale = "LA29519-8",
  PronounMale = "LA29518-0",
}

export enum SnomedCodes {
  AnesthesiaComplication = "33211000",
  TransfusionOfBloodProduct = "43082300",
}

export enum ProcedureStatus {
  Preparation = "preparation",
  InProgress = "in-progress",
  NotDone = "not-done",
  OnHold = "on-hold",
  Stopped = "stopped",
  Completed = "completed",
  EnteredInError = "entered-in-error",
  Unknown = "unknown",
}

export enum ContactPointSystem {
  Phone = "phone",
  Email = "email",
}

export enum ContactPointUse {
  Mobile = "mobile",
  Home = "home",
  Work = "work",
}

export enum AnswersOptions {
  // Identifying Information
  IdentifyingInformationFirstName = "identifyingInformationFirstName",
  IdentifyingInformationMiddleName = "identifyingInformationMiddleName",
  IdentifyingInformationLastName = "identifyingInformationLastName",
  IdentifyingInformationChosenFirstName = "identifyingInformationChosenFirstName",
  IdentifyingInformationPreviousLastName = "identifyingInformationPreviousLastName",
  IdentifyingInformationDOB = "identifyingInformationDOB",
  IdentifyingInformationSSN = "identifyingInformationSSN",

  // Demographic
  DemographicLanguage = "demographicLanguage",
  DemographicLanguageSupport = "demographicLanguageSupport",
  DemographicEthnicity = "demographicEthnicity",
  DemographicRace = "demographicRace",
  DemographicLegalSex = "demographicLegalSex",
  DemographicPronouns = "demographicPronouns",
  DemographicSexAssignedAtBirth = "demographicSexAssignedAtBirth",
  DemographicGenderIdentity = "demographicGenderIdentity",
  DemographicSexualOrientation = "demographicSexualOrientation",

  // Contact Information - Address & Phone Preferences
  ContactInformationAddress = "contactInformationAddress",
  ContactInformationCity = "contactInformationCity",
  ContactInformationState = "contactInformationState",
  ContactInformationZipCode = "contactInformationZipCode",
  ContactInformationPhone = "contactInformationPhoneFormsetPhone",
  ContactInformationType = "contactInformationPhoneFormsetType",
  ContactInformationPreferredContactNumber = "contactInformationPreferredContactNumber",
  ContactInformationVoiceMailContactNumber = "contactInformationVoiceMailContactNumber",

  // Contact Information - Emergency Contact
  ContactInformationEmergencyFirstName = "contactInformationEmergencyFirstName",
  ContactInformationEmergencyLastName = "contactInformationEmergencyLastName",
  ContactInformationEmergencyRelationship = "contactInformationEmergencyRelationship",
  ContactInformationEmergencyPhone = "contactInformationEmergencyPhone",
  ContactInformationEmergencyType = "contactInformationEmergencyType",

  // Billing Information
  BillingInformationPaymentMethod = "billingInformationPaymentMethod",
  BillingInformationHealthInsurance = "billingInformationHealthInsurance",
  BillingInformationInsuranceCompany = "billingInformationInsuranceCompany",
  BillingInformationInsuranceId = "billingInformationInsuranceId",
  BillingInformationInsuranceGroupNumber = "billingInformationInsuranceGroupNumber",
  BillingInformationSubscriberRelationship = "billingInformationSubscriberRelationship",
  BillingInformationSubscriberFirstName = "billingInformationSubscriberFirstName",
  BillingInformationSubscriberLastName = "billingInformationSubscriberLastName",
  BillingInformationSubscriberMiddleName = "billingInformationSubscriberMiddleName",
  BillingInformationSubscriberDateOfBirth = "billingInformationSubscriberDateOfBirth",
  BillingInformationSubscriberID = "billingInformationSubscriberID",
  BillingInformationAddress = "billingInformationAddress",
  BillingInformationState = "billingInformationState",
  BillingInformationZipCode = "billingInformationZipCode",
  BillingInformationPhone = "billingInformationPhone",
  BillingInformationType = "billingInformationType",
  BillingInformationCountry = "billingInformationCountry",
  BillingInformationAddressDetails = "billingInformationAddressDetails",
  BillingInformationInsuranceCardFront = "billingInformationInsuranceCardFront",
  BillingInformationInsuranceCardBack = "billingInformationInsuranceCardBack",
  BillingInformationStateMedicaidProgram = "billingInformationStateMedicaidProgram",
  BillingInformationSecondaryHealthInsurance = "billingInformationSecondaryHealthInsurance",
  BillingInformationEnrolledStateMedicaidProgram = "billingInformationEnrolledStateMedicaidProgram",
  BillingInformationMedicaidProgramName = "billingInformationMedicaidProgramName",
  BillingInformationMedicaidEnroll = "billingInformationMedicaidEnroll",
  BillingInformationReceiveWICAssistance = "billingInformationReceiveWICAssistance",
  BillingInformationInsuranceAndBillingComments = "billingInformationInsuranceAndBillingComments",
  BillingInformationSecondInsuranceCompany = "billingInformationSecondInsuranceCompany",
  BillingInformationSecondInsuranceId = "billingInformationSecondInsuranceId",
  BillingInformationSecondInsuranceGroupNumber = "billingInformationSecondInsuranceGroupNumber",
  BillingInformationSecondInsuranceSubscriberRelationship = "billingInformationSecondInsuranceSubscriberRelationship",
  BillingInformationSecondInsuranceSubscriberFirstName = "billingInformationSecondInsuranceSubscriberFirstName",
  BillingInformationSecondInsuranceSubscriberLastName = "billingInformationSecondInsuranceSubscriberLastName",
  BillingInformationSecondInsuranceSubscriberMiddleName = "billingInformationSecondInsuranceSubscriberMiddleName",
  BillingInformationSecondInsuranceSubscriberDateOfBirth = "billingInformationSecondInsuranceSubscriberDateOfBirth",
  BillingInformationSecondInsuranceSubscriberID = "billingInformationSecondInsuranceSubscriberID",
  BillingInformationSecondInsuranceAddress = "billingInformationSecondInsuranceAddress",
  BillingInformationSecondInsuranceState = "billingInformationSecondInsuranceState",
  BillingInformationSecondInsuranceZipCode = "billingInformationSecondInsuranceZipCode",
  BillingInformationSecondInsurancePhone = "billingInformationSecondInsurancePhone",
  BillingInformationSecondInsuranceType = "billingInformationSecondInsuranceType",
  BillingInformationSecondInsuranceCountry = "billingInformationSecondInsuranceCountry",
  BillingInformationSecondInsuranceAddressDetails = "billingInformationSecondInsuranceAddressDetails",
  BillingInformationSecondInsuranceCardFront = "billingInformationSecondInsuranceCardFront",
  BillingInformationSecondInsuranceCardBack = "billingInformationSecondInsuranceCardBack",

  // Background Information
  BackgroundInformationPlaceOfBirth = "backgroundInformationPlaceOfBirth",
  BackgroundInformationStateOfBirth = "backgroundInformationStateOfBirth",
  BackgroundInformationCountryOfBirth = "backgroundInformationCountryOfBirth",
  BackgroundInformationSpiritualCultureProhibitions = "backgroundInformationSpiritualCultureProhibitions",
  BackgroundInformationSpiritualCultureProhibitionsDetails = "backgroundInformationSpiritualCultureProhibitionsDetails",
  BackgroundInformationSpiritualCultureAcceptBloodProducts = "backgroundInformationSpiritualCultureAcceptBloodProducts",
  BackgroundInformationSpiritualCultureAcceptBloodProductsDetails = "backgroundInformationSpiritualCultureAcceptBloodProductsDetails",
  BackgroundInformationOccupationAndEducationOccupation = "backgroundInformationOccupationAndEducationOccupation",
  BackgroundInformationOccupationAndEducationLevelOfEducation = "backgroundInformationOccupationAndEducationGrade",

  // Support Network
  SupportNetworkPartnerExists = "supportNetworkPartnerExists",
  SupportNetworkPartnerFormset = "supportNetworkPartnerFormset",
  SupportNetworkPartnerFormsetCode = "supportNetworkPartnerFormsetCode",
  SupportNetworkPartnerFormsetMaritalStatus = "supportNetworkPartnerFormsetMaritalStatus",
  SupportNetworkPartnerFormsetIsParent = "supportNetworkPartnerFormsetIsParent",
  SupportNetworkPartnerFormsetFirstName = "supportNetworkPartnerFormsetFirstName",
  SupportNetworkPartnerFormsetMiddleName = "supportNetworkPartnerFormsetMiddleName",
  SupportNetworkPartnerFormsetLastName = "supportNetworkPartnerFormsetLastName",
  SupportNetworkPartnerFormsetChosenFirstName = "supportNetworkPartnerFormsetChosenFirstName",
  SupportNetworkPartnerFormsetDOB = "supportNetworkPartnerFormsetDOB",
  SupportNetworkPartnerFormsetPronouns = "supportNetworkPartnerFormsetPronouns",
  SupportNetworkPartnerFormsetAddress = "supportNetworkPartnerFormsetAddress",
  SupportNetworkPartnerFormsetCity = "supportNetworkPartnerFormsetCity",
  SupportNetworkPartnerFormsetState = "supportNetworkPartnerFormsetState",
  SupportNetworkPartnerFormsetZipCode = "supportNetworkPartnerFormsetZipCode",
  SupportNetworkPartnerFormsetPhone = "supportNetworkPartnerFormsetPhone",
  SupportNetworkPartnerFormsetPhoneType = "supportNetworkPartnerFormsetPhoneType",
  SupportNetworkPartnerFormsetEmail = "supportNetworkPartnerFormsetEmail",
  SupportNetworkPartnerFormsetCanShareInformation = "supportNetworkPartnerFormsetCanShareInformation",
  SupportNetworkPartnerFormsetCoParent = "supportNetworkPartnerFormsetCoParent",
  SupportNetworkPartnerFormsetLivingTogether = "supportNetworkPartnerFormsetLivingTogether",
  SupportNetworkPartnerFormsetWillBeAtBirthCertification = "supportNetworkPartnerFormsetWillBeAtBirthCertification",
  SupportNetworkPartnerFormsetStatusComments = "supportNetworkPartnerFormsetStatusComments",
  SupportNetworkAdultFormset = "supportNetworkAdultFormset",
  SupportNetworkAdultFormsetCode = "supportNetworkAdultFormsetCode",
  SupportNetworkAdultFormsetFirstName = "supportNetworkAdultFormsetFirstName",
  SupportNetworkAdultFormsetLastName = "supportNetworkAdultFormsetLastName",
  SupportNetworkAdultFormsetPronouns = "supportNetworkAdultFormsetPronouns",
  SupportNetworkAdultFormsetRelationship = "supportNetworkAdultFormsetRelationship",

  // General Health
  GeneralHealthHeightFt = "generalHealthHeightFt",
  GeneralHealthHeightIn = "generalHealthHeightIn",
  GeneralHealthPrePregnancyWeight = "generalHealthPrePregnancyWeight",
  GeneralHealthHeightWeightComments = "generalHealthHeightWeightComments",
  GeneralHealthMedicationOccurrence = "generalHealthMedicationOccurrence",
  generalHealthMedicationFormsetTitle = "generalHealthMedicationFormsetTitle",
  GeneralHealthMedicationFormset = "generalHealthMedicationFormset",
  GeneralHealthMedicationFormsetCode = "generalHealthMedicationFormsetCode",
  GeneralHealthMedicationFormsetStatus = "generalHealthMedicationFormsetStatus",
  GeneralHealthMedicationFormsetStartDate = "generalHealthMedicationFormsetStartDate",
  GeneralHealthMedicationFormsetDose = "generalHealthMedicationFormsetDose",
  GeneralHealthMedicationFormsetDoseUnit = "generalHealthMedicationFormsetDoseUnit",
  GeneralHealthMedicationFormsetRoute = "generalHealthMedicationFormsetRoute",
  GeneralHealthMedicationFormsetRouteSpecification = "generalHealthMedicationFormsetRouteSpecification",
  GeneralHealthMedicationFormsetComments = "generalHealthMedicationFormsetComments",
  GeneralHealthAllergiesFormset = "generalHealthAllergiesFormset",
  GeneralHealthAllergiesFormsetCode = "generalHealthAllergiesFormsetCode",
  GeneralHealthAllergiesDetails = "generalHealthAllergiesDetails",
  GeneralHealthAllergiesStatus = "generalHealthAllergiesStatus",
  GeneralHealthAllergiesDate = "generalHealthAllergiesDate",
  GeneralHealthAllergiesReaction = "generalHealthAllergiesReaction",
  GeneralHealthAllergiesSeverity = "generalHealthAllergiesSeverity",
  GeneralHealthAllergiesComments = "generalHealthAllergiesComments",
  GeneralHealthMedicalProblemsCirculatory = "generalHealthMedicalProblemsCirculatory",
  GeneralHealthMedicalProblemsChildhoodDiseases = "generalHealthMedicalProblemsChildhoodDiseases",
  GeneralHealthMedicalProblemsImmuneEndocrine = "generalHealthMedicalProblemsImmuneEndocrine",
  GeneralHealthMedicalProblemsDigestive = "generalHealthMedicalProblemsDigestive",
  GeneralHealthMedicalProblemsUrinary = "generalHealthMedicalProblemsUrinary",
  GeneralHealthMedicalProblemsNervous = "generalHealthMedicalProblemsNervous",
  GeneralHealthMedicalProblemsSexualHealth = "generalHealthMedicalProblemsSexualHealth",
  GeneralHealthMedicalProblemsEmotionalPsychiatric = "generalHealthMedicalProblemsEmotionalPsychiatric",
  GeneralHealthMedicalProblemsAdditionalProblems = "generalHealthMedicalProblemsAdditionalProblems",
  GeneralHealthMedicalProblemsComments = "generalHealthMedicalProblemsComments",
  GeneralHealthSurgeriesOccurrence = "generalHealthSurgeriesOccurrence",
  GeneralHealthSurgeriesFormset = "generalHealthSurgeriesFormset",
  GeneralHealthSurgeriesFormsetCode = "generalHealthSurgeriesFormsetCode",
  GeneralHealthSurgeriesFormsetStatus = "generalHealthSurgeriesFormsetStatus",
  GeneralHealthSurgeriesFormsetDate = "generalHealthSurgeriesFormsetDate",
  GeneralHealthSurgeriesFormsetType = "generalHealthSurgeriesFormsetType",
  GeneralHealthSurgeriesFormsetComments = "generalHealthSurgeriesFormsetComments",
  GeneralHealthSurgeriesFormsetBloodTransfusion = "generalHealthSurgeriesFormsetBloodTransfusion",
  GeneralHealthSurgeriesFormsetAnesthesiaProblems = "generalHealthSurgeriesFormsetAnesthesiaProblems",
  GeneralHealthOtherSignificantFindingsFormset = "generalHealthOtherSignificantFindingsFormset",
  GeneralHealthOtherSignificantFindingsFormsetCode = "generalHealthOtherSignificantFindingsFormsetCode",
  GeneralHealthOtherSignificantFindingsComments = "generalHealthOtherSignificantFindingsComments",
  GeneralHealthOtherSignificantFindingsName = "generalHealthOtherSignificantFindingsName",
  GeneralHealthOtherSignificantFindingsDate = "generalHealthOtherSignificantFindingsDate",
  GeneralHealthOtherSignificantFindingsStatus = "generalHealthOtherSignificantFindingsStatus",

  // Gynecological Health
  GynecologicalHealthLastPapTestDate = "gynecologicalHealthLastPapTestDate",
  GynecologicalHealthLastPapTestResult = "gynecologicalHealthLastPapTestResult",
  GynecologicalSexualHealthPapTestPreviousAbnormalResult = "gynecologicalSexualHealthPapTestPreviousAbnormalResult",
  GynecologicalSexualHealthPapTestPreviousAbnormalProblem = "gynecologicalSexualHealthPapTestPreviousAbnormalProblem",
  GynecologicalSexualHealthPapTestPreviousAbnormalTreatment = "gynecologicalSexualHealthPapTestPreviousAbnormalTreatment",
  GynecologicalHealthPreviousToLastPapTestResult = "gynecologicalHealthPreviousToLastPapTestResult",
  GynecologicalHealthPreviousToLastPapTestAbnormalProblem = "gynecologicalHealthPreviousToLastPapTestAbnormalProblem",
  GynecologicalHealthPreviousToLastPapTestAbnormalTreatment = "gynecologicalHealthPreviousToLastPapTestAbnormalTreatment",
  GynecologicalHealthPapTestComments = "gynecologicalHealthPapTestComments",
  GynecologicalHealthBirthControlUse = "gynecologicalHealthBirthControlUse",
  GynecologicalHealthBirthControlMethod = "gynecologicalHealthBirthControlMethod",
  GynecologicalHealthBirthControlMethodFormset = "gynecologicalHealthBirthControlMethodFormset",
  GynecologicalHealthBirthControlMethodFormsetCode = "gynecologicalHealthBirthControlMethodFormsetCode",
  GynecologicalHealthBirthControlMethodFormsetNotes = "gynecologicalHealthBirthControlMethodFormsetNotes",
  GynecologicalHealthBirthControlPastUse = "gynecologicalHealthBirthControlPastUse",
  GynecologicalHealthBirthControlPastUseMethodFormset = "gynecologicalHealthBirthControlPastUseMethodFormset",
  GynecologicalHealthBirthControlPastUseMethodFormsetCode = "gynecologicalHealthBirthControlPastUseMethodFormsetCode",
  GynecologicalHealthBirthControlPastUseMethodFormsetNotes = "gynecologicalHealthBirthControlPastUseMethodFormsetNotes",
  GynecologicalHealthBirthControlPastUseMethod = "gynecologicalHealthBirthControlPastUseMethod",
  GynecologicalHealthBirthControlComments = "gynecologicalHealthBirthControlComments",
  GynecologicalHealthBreastSelfExams = "gynecologicalHealthBreastSelfExams",
  GynecologicalHealthBreastSelfExamsFrequency = "gynecologicalHealthBreastSelfExamsFrequency",
  GynecologicalHealthMammogram = "gynecologicalHealthMammogram",
  GynecologicalHealthMammogramDate = "gynecologicalHealthMammogramDate",
  GynecologicalHealthMammogramResult = "gynecologicalHealthMammogramResult",
  GynecologicalHealthBreastfeeding = "gynecologicalHealthBreastfeeding",
  GynecologicalHealthBreastfeedingMonths = "gynecologicalHealthBreastfeedingMonths",
  GynecologicalHealthBreastBiopsy = "gynecologicalHealthBreastBiopsy",
  GynecologicalHealthBreastBiopsyDate = "gynecologicalHealthBreastBiopsyDate",
  GynecologicalHealthBreastBiopsyResult = "gynecologicalHealthBreastBiopsyResult",
  GynecologicalHealthBreastSurgery = "gynecologicalHealthBreastSurgery",
  GynecologicalHealthBreastSurgeryType = "gynecologicalHealthBreastSurgeryType",
  GynecologicalHealthBreastSurgeryTypeOther = "gynecologicalHealthBreastSurgeryTypeOther",
  GynecologicalHealthBreastHealthComments = "gynecologicalHealthBreastHealthComments",

  // Lifestyle
  LifestyleAlcohol = "lifestyleAlcohol",
  LifestyleAlcoholFrequency = "lifestyleAlcoholFrequency",
  LifestyleSmokingEver = "lifestyleSmokingEver",
  LifestyleSmoking = "lifestyleSmoking",
  LifestyleDrugsOther = "lifestyleDrugsOther",
  LifestyleDrugsOtherFrequency = "lifestyleDrugsOtherFrequency",
  LifestyleSmokingHousehold = "lifestyleSmokingHousehold",
  LifestyleSmokingHouseholdFrequency = "lifestyleSmokingHouseholdFrequency",
  LifestyleSubstanceUseComments = "lifestyleSubstanceUseComments",
  LifestyleDietaryRestrictions = "lifestyleDietaryRestrictions",
  LifestyleSodaFrequency = "lifestyleSodaFrequency",
  LifestyleCaffeineFrequency = "lifestyleCaffeineFrequency",
  LifestyleProcessedFoodsFrequency = "lifestyleProcessedFoodsFrequency",
  LifestyleNutritionComments = "lifestyleNutritionComments",
  LifestyleExercise = "lifestyleExercise",
  LifestyleExerciseComments = "lifestyleExerciseComments",
  LifestyleStressFeelings = "lifestyleStressFeelings",
  LifestyleStressFinancialStress = "lifestyleStressFinancialStress",
  LifestyleStressComments = "lifestyleStressComments",
  LifestyleSafetyEmotionalAbuse = "lifestyleSafetyEmotionalAbuse",
  LifestyleSafetyFear = "lifestyleSafetyFear",
  LifestyleSafetySexualAbuse = "lifestyleSafetySexualAbuse",
  LifestyleSafetyPhysicalAbuse = "lifestyleSafetyPhysicalAbuse",
  LifestyleSafetyComments = "lifestyleSafetyComments",

  // Current Pregnancy
  CurrentPregnancyLastMenstrualPeriod = "currentPregnancyLastMenstrualPeriod",
  CurrentPregnancyLastMenstrualPeriodAmount = "currentPregnancyLastMenstrualPeriodAmount",
  CurrentPregnancyLastMenstrualPeriodAmountDetails = "currentPregnancyLastMenstrualPeriodAmountDetails",
  CurrentPregnancyLastMenstrualPeriodDuration = "currentPregnancyLastMenstrualPeriodDuration",
  CurrentPregnancyLastMenstrualPeriodDurationDetails = "currentPregnancyLastMenstrualPeriodDurationDetails",
  CurrentPregnancyLastMenstrualPeriodComments = "currentPregnancyLastMenstrualPeriodComments",
  CurrentPregnancySpotting = "currentPregnancySpotting",
  CurrentPregnancySpottingDetails = "currentPregnancySpottingDetails",
  CurrentPregnancyRegularPeriods = "currentPregnancyRegularPeriods",
  CurrentPregnancyCycleLength = "currentPregnancyCycleLength",
  CurrentPregnancyPeriodLength = "currentPregnancyPeriodLength",
  CurrentPregnancyAgeMenarche = "currentPregnancyAgeMenarche",
  CurrentPregnancyHormonalBirthControl = "currentPregnancyHormonalBirthControl",
  CurrentPregnancyBreastfeeding = "currentPregnancyBreastfeeding",
  CurrentPregnancyPregnant = "currentPregnancyPregnant",
  CurrentPregnancyMenstrualHistoryComments = "currentPregnancyMenstrualHistoryComments",
  CurrentPregnancyConceptionKnown = "currentPregnancyConceptionKnown",
  CurrentPregnancyConceptionDate = "currentPregnancyConceptionDate",
  CurrentPregnancyPlanned = "currentPregnancyPlanned",
  CurrentPregnancyFetalMovement = "currentPregnancyFetalMovement",
  CurrentPregnancyFetalMovementDate = "currentPregnancyFetalMovementDate",
  CurrentPregnancyUltrasound = "currentPregnancyUltrasound",
  CurrentPregnancyUltrasoundDate = "currentPregnancyUltrasoundDate",
  CurrentPregnancyFertilityTreatments = "currentPregnancyFertilityTreatments",
  CurrentPregnancyFertilityTreatmentsDetails = "currentPregnancyFertilityTreatmentsDetails",
  CurrentPregnancyConceptionComments = "currentPregnancyConceptionComments",
  CurrentPregnancyPositiveTest = "currentPregnancyPositiveTest",
  CurrentPregnancyPositiveTestDate = "currentPregnancyPositiveTestDate",
  CurrentPregnancyPositiveTestType = "currentPregnancyPositiveTestType",
  CurrentPregnancyGeneticScreening = "currentPregnancyGeneticScreening",
  CurrentPregnancyGeneticScreeningDate = "currentPregnancyGeneticScreeningDate",
  CurrentPregnancyTestsComments = "currentPregnancyTestsComments",
  CurrentPregnancySymptomsExperienced = "currentPregnancySymptomsExperienced",
  CurrentPregnancySymptomsComments = "currentPregnancySymptomsComments",
  CurrentPregnancyGeneticScreeningDetails = "currentPregnancyGeneticScreeningDetails",
  CurrentPregnancyBirthLocationCoding = "currentPregnancyBirthLocationCoding",
  CurrentPregnancyBirthLocationTypeOther = "currentPregnancyBirthLocationTypeOther",

  // Past Pregnancies
  pastPregnanciesOccurrences = "pastPregnanciesOccurrences",
  PastPregnanciesFullTerm = "pastPregnanciesFullTerm",
  PastPregnanciesPremature = "pastPregnanciesPremature",
  PastPregnanciesAbortions = "pastPregnanciesAbortions",
  PastPregnanciesMiscarriages = "pastPregnanciesMiscarriages",
  PastPregnanciesAbortionsAndMiscarriages = "pastPregnanciesTotalAbortions",
  PastPregnanciesLivingChildren = "pastPregnanciesLivingChildren",
  PastPregnanciesComments = "pastPregnanciesComments",
  PastPregnanciesHistoryFormset = "pastPregnanciesHistoryFormset",
  PastPregnanciesHistoryFormsetCode = "pastPregnanciesHistoryFormsetCode",
  pastPregnanciesHistoryFormsetFetusesCount = "pastPregnanciesHistoryFormsetFetusesCount",
  pastPregnanciesHistoryFormsetOutcome = "pastPregnanciesHistoryFormsetOutcome",
  pastPregnanciesHistoryFormsetGeneticParent = "pastPregnanciesHistoryFormsetGeneticParent",
  pastPregnanciesHistoryFormsetAdditionalFacts = "pastPregnanciesHistoryFormsetAdditionalFacts",

  // Family Medical History
  FamilyMedicalHistoryProblems = "familyMedicalHistoryProblems",
  FamilyMedicalHistoryProblemsFormset = "familyMedicalHistoryProblemsFormset",
  FamilyMedicalHistoryProblemsFormsetCode = "familyMedicalHistoryProblemsFormsetCode",
  FamilyMedicalHistoryProblemsFormsetFamilyMember = "familyMedicalHistoryProblemsFormsetFamilyMember",
  FamilyMedicalHistoryProblemsFormsetAgeOfOnset = "familyMedicalHistoryProblemsFormsetAgeOfOnset",
  FamilyMedicalHistoryProblemsFormsetStatus = "familyMedicalHistoryProblemsFormsetStatus",
  FamilyMedicalHistoryProblemsFormsetOnsetDate = "familyMedicalHistoryProblemsFormsetOnsetDate",
  FamilyMedicalHistoryProblemsFormsetComments = "familyMedicalHistoryProblemsFormsetComments",
  FamilyMedicalHistoryProblemsComments = "familyMedicalHistoryProblemsComments",
  FamilyMedicalHistoryPregnancyProblems = "familyMedicalHistoryPregnancyProblems",
  FamilyMedicalHistoryPregnancyProblemsFormset = "familyMedicalHistoryPregnancyProblemsFormset",
  FamilyMedicalHistoryPregnancyProblemsFormsetCode = "familyMedicalHistoryPregnancyProblemsFormsetCode",
  FamilyMedicalHistoryPregnancyProblemsFormsetFamilyMember = "familyMedicalHistoryPregnancyProblemsFormsetFamilyMember",
  FamilyMedicalHistoryPregnancyProblemsFormsetAgeOfOnset = "familyMedicalHistoryPregnancyProblemsFormsetAgeOfOnset",
  FamilyMedicalHistoryPregnancyProblemsFormsetStatus = "familyMedicalHistoryPregnancyProblemsFormsetStatus",
  FamilyMedicalHistoryPregnancyProblemsFormsetOnsetDate = "familyMedicalHistoryPregnancyProblemsFormsetOnsetDate",
  FamilyMedicalHistoryPregnancyProblemsFormsetComments = "familyMedicalHistoryPregnancyProblemsFormsetComments",
  FamilyMedicalHistoryPregnancyProblemsComments = "familyMedicalHistoryPregnancyProblemsComments",

  // Circulatory Medical Problems
  GeneralHealthMedicalProblemsCirculatoryFormset = "generalHealthMedicalProblemsCirculatoryFormset",
  GeneralHealthMedicalProblemsCirculatoryFormsetCode = "generalHealthMedicalProblemsCirculatoryFormsetCode",
  GeneralHealthMedicalProblemsCirculatoryFormsetStatus = "generalHealthMedicalProblemsCirculatoryFormsetStatus",
  GeneralHealthMedicalProblemsCirculatoryFormsetOnsetDate = "generalHealthMedicalProblemsCirculatoryFormsetOnsetDate",
  GeneralHealthMedicalProblemsCirculatoryFormsetComments = "generalHealthMedicalProblemsCirculatoryFormsetComments",

  // Respiratory Medical Problems
  GeneralHealthMedicalProblemsRespiratory = "generalHealthMedicalProblemsRespiratory",
  GeneralHealthMedicalProblemsRespiratoryFormset = "generalHealthMedicalProblemsRespiratoryFormset",
  GeneralHealthMedicalProblemsRespiratoryFormsetCode = "generalHealthMedicalProblemsRespiratoryFormsetCode",
  GeneralHealthMedicalProblemsRespiratoryFormsetStatus = "generalHealthMedicalProblemsRespiratoryFormsetStatus",
  GeneralHealthMedicalProblemsRespiratoryFormsetOnsetDate = "generalHealthMedicalProblemsRespiratoryFormsetOnsetDate",
  GeneralHealthMedicalProblemsRespiratoryFormsetComments = "generalHealthMedicalProblemsRespiratoryFormsetComments",

  // Childhood Diseases
  GeneralHealthMedicalProblemsChildhoodDiseasesFormset = "generalHealthMedicalProblemsChildhoodDiseasesFormset",
  GeneralHealthMedicalProblemsChildhoodDiseasesFormsetCode = "generalHealthMedicalProblemsChildhoodDiseasesFormsetCode",
  GeneralHealthMedicalProblemsChildhoodDiseasesFormsetOnsetDate = "generalHealthMedicalProblemsChildhoodDiseasesFormsetOnsetDate",
  GeneralHealthMedicalProblemsChildhoodDiseasesFormsetStatus = "generalHealthMedicalProblemsChildhoodDiseasesFormsetStatus",
  GeneralHealthMedicalProblemsChildhoodDiseasesFormsetComments = "generalHealthMedicalProblemsChildhoodDiseasesFormsetComments",

  // Immune/Endocrine
  GeneralHealthMedicalProblemsImmuneEndocrineFormset = "generalHealthMedicalProblemsImmuneEndocrineFormset",
  GeneralHealthMedicalProblemsImmuneEndocrineFormsetCode = "generalHealthMedicalProblemsImmuneEndocrineFormsetCode",
  GeneralHealthMedicalProblemsImmuneEndocrineFormsetOnsetDate = "generalHealthMedicalProblemsImmuneEndocrineFormsetOnsetDate",
  GeneralHealthMedicalProblemsImmuneEndocrineFormsetStatus = "generalHealthMedicalProblemsImmuneEndocrineFormsetStatus",
  GeneralHealthMedicalProblemsImmuneEndocrineFormsetComments = "generalHealthMedicalProblemsImmuneEndocrineFormsetComments",

  // Digestive
  GeneralHealthMedicalProblemsDigestiveFormset = "generalHealthMedicalProblemsDigestiveFormset",
  GeneralHealthMedicalProblemsDigestiveFormsetCode = "generalHealthMedicalProblemsDigestiveFormsetCode",
  GeneralHealthMedicalProblemsDigestiveFormsetStatus = "generalHealthMedicalProblemsDigestiveFormsetStatus",
  GeneralHealthMedicalProblemsDigestiveFormsetOnsetDate = "generalHealthMedicalProblemsDigestiveFormsetOnsetDate",
  GeneralHealthMedicalProblemsDigestiveFormsetComments = "generalHealthMedicalProblemsDigestiveFormsetComments",

  // Urinary
  GeneralHealthMedicalProblemsUrinaryFormset = "generalHealthMedicalProblemsUrinaryFormset",
  GeneralHealthMedicalProblemsUrinaryFormsetCode = "generalHealthMedicalProblemsUrinaryFormsetCode",
  GeneralHealthMedicalProblemsUrinaryFormsetStatus = "generalHealthMedicalProblemsUrinaryFormsetStatus",
  GeneralHealthMedicalProblemsUrinaryFormsetOnsetDate = "generalHealthMedicalProblemsUrinaryFormsetOnsetDate",
  GeneralHealthMedicalProblemsUrinaryFormsetComments = "generalHealthMedicalProblemsUrinaryFormsetComments",

  // Nervous
  GeneralHealthMedicalProblemsNervousFormset = "generalHealthMedicalProblemsNervousFormset",
  GeneralHealthMedicalProblemsNervousFormsetCode = "generalHealthMedicalProblemsNervousFormsetCode",
  GeneralHealthMedicalProblemsNervousFormsetOnsetDate = "generalHealthMedicalProblemsNervousFormsetOnsetDate",
  GeneralHealthMedicalProblemsNervousFormsetStatus = "generalHealthMedicalProblemsNervousFormsetStatus",
  GeneralHealthMedicalProblemsNervousFormsetComments = "generalHealthMedicalProblemsNervousFormsetComments",

  // Sexual Health
  GeneralHealthMedicalProblemsSexualHealthFormset = "generalHealthMedicalProblemsSexualHealthFormset",
  GeneralHealthMedicalProblemsSexualHealthFormsetCode = "generalHealthMedicalProblemsSexualHealthFormsetCode",
  GeneralHealthMedicalProblemsSexualHealthFormsetOnsetDate = "generalHealthMedicalProblemsSexualHealthFormsetOnsetDate",
  GeneralHealthMedicalProblemsSexualHealthFormsetStatus = "generalHealthMedicalProblemsSexualHealthFormsetStatus",
  GeneralHealthMedicalProblemsSexualHealthFormsetComments = "generalHealthMedicalProblemsSexualHealthFormsetComments",

  // Emotional/Psychiatric
  GeneralHealthMedicalProblemsEmotionalPsychiatricFormset = "generalHealthMedicalProblemsEmotionalPsychiatricFormset",
  GeneralHealthMedicalProblemsEmotionalPsychiatricFormsetCode = "generalHealthMedicalProblemsEmotionalPsychiatricFormsetCode",
  GeneralHealthMedicalProblemsEmotionalPsychiatricFormsetOnsetDate = "generalHealthMedicalProblemsEmotionalPsychiatricFormsetOnsetDate",
  GeneralHealthMedicalProblemsEmotionalPsychiatricFormsetStatus = "generalHealthMedicalProblemsEmotionalPsychiatricFormsetStatus",
  GeneralHealthMedicalProblemsEmotionalPsychiatricFormsetComments = "generalHealthMedicalProblemsEmotionalPsychiatricFormsetComments",

  // Additional (select only, no formset in questionnaire)
  GeneralHealthMedicalProblemsAdditionalProblemsFormset = "generalHealthMedicalProblemsAdditionalProblemsFormset",
  GeneralHealthMedicalProblemsAdditionalProblemsFormsetCode = "generalHealthMedicalProblemsAdditionalProblemsFormsetCode",
  GeneralHealthMedicalProblemsAdditionalProblemsFormsetOnsetDate = "generalHealthMedicalProblemsAdditionalProblemsFormsetOnsetDate",
  GeneralHealthMedicalProblemsAdditionalProblemsFormsetStatus = "generalHealthMedicalProblemsAdditionalProblemsFormsetStatus",
  GeneralHealthMedicalProblemsAdditionalProblemsFormsetComments = "generalHealthMedicalProblemsAdditionalProblemsFormsetComments",
}

const QHDisplay = {
  [AnswersOptions.pastPregnanciesOccurrences]: "Total past pregnancies",
  [AnswersOptions.pastPregnanciesHistoryFormsetFetusesCount]:
    "Past pregnancy history fetuses count",
  [AnswersOptions.pastPregnanciesHistoryFormsetGeneticParent]:
    "Genetic parent of this past pregnancy is the same as for the current pregnancy",
  [AnswersOptions.PastPregnanciesFullTerm]: "Full term pregnancies",
  [AnswersOptions.PastPregnanciesPremature]: "Premature births",
  [AnswersOptions.PastPregnanciesAbortions]: "Abortions",
  [AnswersOptions.PastPregnanciesMiscarriages]: "Miscarriages",
  [AnswersOptions.PastPregnanciesAbortionsAndMiscarriages]: "Total abortions and miscarriages",
  [AnswersOptions.PastPregnanciesLivingChildren]: "Living children",
  [AnswersOptions.PastPregnanciesComments]: "Past pregnancies comments",
} as const;

export const ObservationCode: Record<string, CodeableConcept> = {
  [AnswersOptions.pastPregnanciesOccurrences]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.pastPregnanciesOccurrences,
        display: QHDisplay[AnswersOptions.pastPregnanciesOccurrences],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.TotalPregnancies,
        display: LOINCDisplay[LOINCCodes.TotalPregnancies],
      },
    ],
  },
  [AnswersOptions.pastPregnanciesHistoryFormsetFetusesCount]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.pastPregnanciesHistoryFormsetFetusesCount,
        display: QHDisplay[AnswersOptions.pastPregnanciesHistoryFormsetFetusesCount],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.NumberOfFetuses,
        display: LOINCDisplay[LOINCCodes.NumberOfFetuses],
      },
    ],
  },
  [AnswersOptions.pastPregnanciesHistoryFormsetGeneticParent]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.pastPregnanciesHistoryFormsetGeneticParent,
        display: QHDisplay[AnswersOptions.pastPregnanciesHistoryFormsetGeneticParent],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesComments]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesComments,
        display: QHDisplay[AnswersOptions.PastPregnanciesComments],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.OutcomeOfPregnancy,
        display: LOINCDisplay[LOINCCodes.OutcomeOfPregnancy],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesAbortions]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesAbortions,
        display: QHDisplay[AnswersOptions.PastPregnanciesAbortions],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.TotalAbortionsInduced,
        display: LOINCDisplay[LOINCCodes.TotalAbortionsInduced],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesPremature]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesPremature,
        display: QHDisplay[AnswersOptions.PastPregnanciesPremature],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.TotalPremature,
        display: LOINCDisplay[LOINCCodes.TotalPremature],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesFullTerm]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesFullTerm,
        display: QHDisplay[AnswersOptions.PastPregnanciesFullTerm],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.TotalFullTerm,
        display: LOINCDisplay[LOINCCodes.TotalFullTerm],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesMiscarriages]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesMiscarriages,
        display: QHDisplay[AnswersOptions.PastPregnanciesMiscarriages],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.TotalMiscarriage,
        display: LOINCDisplay[LOINCCodes.TotalMiscarriage],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesAbortionsAndMiscarriages]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesAbortionsAndMiscarriages,
        display: QHDisplay[AnswersOptions.PastPregnanciesAbortionsAndMiscarriages],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.TotalAbortionsAndMiscarriages,
        display: LOINCDisplay[LOINCCodes.TotalAbortionsAndMiscarriages],
      },
    ],
  },
  [AnswersOptions.PastPregnanciesLivingChildren]: {
    coding: [
      {
        system: Systems.QH,
        code: AnswersOptions.PastPregnanciesLivingChildren,
        display: QHDisplay[AnswersOptions.PastPregnanciesLivingChildren],
      },
      {
        system: "http://loinc.org",
        code: LOINCCodes.TotalLivingChildren,
        display: LOINCDisplay[LOINCCodes.TotalLivingChildren],
      },
    ],
  },
};

export async function processAllSections(
  medplum: MedplumClient,
  questionnaireResponse: QuestionnaireResponse,
  sentry: SentryInstance,
): Promise<boolean> {
  // Stop on the first section that returns false. Identifying information returns false on
  // bundle failure; MRN remediation after a successful bundle is best-effort (Sentry + continue).
  const sectionProcessors = [
    () => processIdentifyingInformation(medplum, questionnaireResponse, sentry),
    () => processDemographic(medplum, questionnaireResponse, sentry),
    () => processContactInformation(medplum, questionnaireResponse, sentry),
    () => processBillingInformation(medplum, questionnaireResponse, sentry),
    () => processBackgroundInformation(medplum, questionnaireResponse, sentry),
    () => processSupportNetwork(medplum, questionnaireResponse, sentry),
    () => processGeneralHealth(medplum, questionnaireResponse, sentry),
    () => processGynecologicalSexualHealth(medplum, questionnaireResponse, sentry),
    () => processLifestyle(medplum, questionnaireResponse, sentry),
    () => processCurrentPregnancy(medplum, questionnaireResponse, sentry),
    () => processPastPregnancies(medplum, questionnaireResponse, sentry),
    () => processFamilyMedicalHistory(medplum, questionnaireResponse, sentry),
  ];

  for (const processSection of sectionProcessors) {
    const sectionSucceeded = await processSection();
    if (!sectionSucceeded) {
      return false;
    }
  }

  return true;
}

function get(items: QuestionnaireResponseItemAnswer[], position: number = 0) {
  const item = items?.[position];
  if (!item) return;

  const availableKey = ANSWER_TYPES.find((key) => !isEmpty(item[key]));
  if (!availableKey) return;

  return item[availableKey];
}

function convertYesNoToBoolean(value: YesNoOrDontKnow) {
  return value === YesNoOrDontKnow.Yes;
}

export function questionnaireResponseIsAccepted(
  items: QuestionnaireResponseItem[] | undefined,
): boolean {
  if (!items || items.length === 0) {
    return false;
  }

  for (const item of items) {
    if (!item.item) {
      if (!(getExtensionValue(item, Extensions.QuestionnaireResponseItemIsAccepted) as boolean)) {
        return false;
      }
    } else if (!questionnaireResponseIsAccepted(item.item)) {
      return false;
    }
  }

  return true;
}

export function getRequiredQuestionnaireQuestionsLinkIds(
  questionnaireItem: QuestionnaireItem[] | undefined,
  linkIds: Set<string> = new Set<string>(),
): Set<string> {
  if (!questionnaireItem || questionnaireItem.length === 0) {
    return linkIds;
  }

  for (const item of questionnaireItem) {
    if (!item.item) {
      if (item.required) {
        linkIds.add(item.linkId);
      }
    } else {
      getRequiredQuestionnaireQuestionsLinkIds(item.item, linkIds);
    }
  }

  return linkIds;
}

export function getAnsweredQuestionnaireResponseItemsLinkIds(
  questionnaireResponseItem: QuestionnaireResponseItem[] | undefined,
  linkIds: Set<string> = new Set<string>(),
): Set<string> {
  if (!questionnaireResponseItem || questionnaireResponseItem.length === 0) {
    return linkIds;
  }

  for (const item of questionnaireResponseItem) {
    if (!item.item) {
      if (item.answer && item.answer.length > 0) {
        linkIds.add(item.linkId);
      }
    } else {
      getAnsweredQuestionnaireResponseItemsLinkIds(item.item, linkIds);
    }
  }

  return linkIds;
}

export function areAllRequiredQuestionsAnswered(
  questionnaireItems: QuestionnaireItem[] | undefined,
  questionnaireResponseItems: QuestionnaireResponseItem[] | undefined,
): boolean {
  const requiredQuestions: Set<string> =
    getRequiredQuestionnaireQuestionsLinkIds(questionnaireItems);
  const answeredQuestions: Set<string> = getAnsweredQuestionnaireResponseItemsLinkIds(
    questionnaireResponseItems,
  );

  // TODO: instead of a loop we could use requiredQuestions.isSubsetOf(answeredQuestions); but the method is
  //  still marked as experimental.
  for (const required of requiredQuestions) {
    if (!answeredQuestions.has(required)) {
      return false;
    }
  }

  return true;
}

export function markModifiedQuestionnaireResponseItemsAsUnaccepted(
  modifiedEntries: Record<string, string>,
  questionnaireResponseItems: QuestionnaireResponseItem[] | undefined,
) {
  if (!questionnaireResponseItems || questionnaireResponseItems.length === 0) {
    return;
  }

  for (const item of questionnaireResponseItems) {
    if (!item.item && item.answer && modifiedEntries[item.linkId]) {
      const modifiedValue = modifiedEntries[item.linkId];
      let answer = get(item.answer!);
      if ((answer as Coding)?.code) {
        answer = (answer as Coding).code;
      }
      if (answer !== modifiedValue) {
        item.extension = [
          ...(item.extension?.filter(
            (extension) => extension.url !== Extensions.QuestionnaireResponseItemIsAccepted,
          ) ?? []),
          {
            url: Extensions.QuestionnaireResponseItemIsAccepted,
            valueBoolean: false,
          },
        ];
      }
    } else {
      markModifiedQuestionnaireResponseItemsAsUnaccepted(modifiedEntries, item.item);
    }
  }
}

export type UnflattenedValueChangesIntakeObject = Record<string, string | Coding | object>;

export function flattenValueChangesIntakeObject(
  obj: UnflattenedValueChangesIntakeObject,
  result: Record<string, string> = {},
): Record<string, string> {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (typeof value === "object" && value !== null) {
        if (
          "valueCoding" in value &&
          typeof value.valueCoding === "object" &&
          value.valueCoding !== null &&
          "code" in value.valueCoding
        ) {
          result[key] = `${value.valueCoding.code}`;
        } else {
          flattenValueChangesIntakeObject(
            value as unknown as UnflattenedValueChangesIntakeObject,
            result,
          );
        }
      } else {
        result[key] = value;
      }
    }
  }
  return result;
}

/**
 * Finds the answers to a group of items that can be repeated.
 *
 * @param response - The QuestionnaireResponse resource where the answers will be extracted
 * @param linkId - The linkId of the group that can be repeated in the questionnaire
 * @param groupByLinkId - The linkId to use as the groupBy key when grouping answers
 * @returns - An array of objects each containing a set of grouped answers
 */
export function getFormsetAnswers(
  response: QuestionnaireResponse,
  linkId: string,
  groupByLinkId: string,
): Record<string, Record<string, QuestionnaireResponseItemAnswer>[]> {
  const findQuestionnaireResponseItems = (
    questionnaireResponseItems: QuestionnaireResponseItem[] | undefined,
    linkId: string,
  ): QuestionnaireResponseItem[] => {
    const result: QuestionnaireResponseItem[] = [];

    for (const item of questionnaireResponseItems ?? []) {
      if (item.linkId === linkId) {
        result.push(item);
      }
      if (item.item) {
        result.push(...findQuestionnaireResponseItems(item.item, linkId));
      }
    }

    return result;
  };

  const extractAnswers = (items: QuestionnaireResponseItem[]) => {
    const answers: Record<string, QuestionnaireResponseItemAnswer> = {};

    for (const { linkId, answer, item } of items) {
      if (item) {
        const subGroupAnswers: Record<string, QuestionnaireResponseItemAnswer> = {};
        item.forEach((subItem) => {
          if (subItem.answer) {
            subGroupAnswers[subItem.linkId] = subItem.answer?.[0] ?? {};
          }
        });
        answers[linkId] = subGroupAnswers;
      } else {
        answers[linkId] = answer?.[0] ?? {};
      }
    }

    return answers;
  };

  const questionnaireResponseItems = findQuestionnaireResponseItems(response.item, linkId);

  if (!questionnaireResponseItems.length) {
    return {};
  }

  const result: Record<string, Record<string, QuestionnaireResponseItemAnswer>[]> = {};

  // Maps answers by the groupByLinkId
  for (const questionnaireResponseItem of questionnaireResponseItems) {
    const answer = extractAnswers(questionnaireResponseItem.item || []);
    const identifier = get([answer[groupByLinkId]]);
    let key;
    if ((identifier as Coding)?.code) {
      key = (identifier as Coding).code?.toString() ?? "";
    } else {
      key = identifier?.toString() ?? "";
    }

    if (!result[key]) {
      result[key] = [];
    }

    result[key].push(answer);
  }

  return result;
}

export function getQuestionnaireFormsetResponses(
  questionnaireResponse: QuestionnaireResponse,
  formsetName: string,
  formsetCode: string,
): Record<string, QuestionnaireResponseItemAnswer>[] {
  return Object.values(getFormsetAnswers(questionnaireResponse, formsetName, formsetCode))
    .map((answer) => answer[0])
    .filter((answer) => !isEmpty(answer));
}

export function getAnswer(
  response: QuestionnaireResponse,
  linkId: string,
): QuestionnaireResponseItemAnswer | undefined {
  const findItem = (
    items: QuestionnaireResponseItem[] | undefined,
    targetLinkId: string,
  ): QuestionnaireResponseItem | undefined => {
    for (const item of items ?? []) {
      if (item.linkId === targetLinkId) return item;
      if (item.item) {
        const found = findItem(item.item, targetLinkId);
        if (found) return found;
      }
    }
    return undefined;
  };

  const item = findItem(response.item, linkId);
  return item?.answer?.[0];
}

type Answer = Record<string, QuestionnaireResponseItemAnswer>;

export class AnswerBuilder {
  constructor(private answers: Answer) {}

  private getFirstValue(key: string): unknown {
    const item = this.answers[key];
    if (!item) return;

    const valueKey = ANSWER_TYPES.find((k) => !isEmpty(item[k]));
    return valueKey ? item[valueKey] : undefined;
  }

  valueString(key: string): string | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "string" ? val : undefined;
  }

  valueInteger(key: string): number | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "number" ? val : undefined;
  }

  valueDate(key: string): string | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val) ? val : undefined;
  }

  valueBoolean(key: string): boolean | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "boolean" ? val : undefined;
  }

  valueCoding(key: string): Coding | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "object" && val !== null && "code" in val ? (val as Coding) : undefined;
  }

  valueYesNoToBoolean(key: string): boolean | undefined {
    const val = this.valueString(key);
    return val ? convertYesNoToBoolean(val as YesNoOrDontKnow) : undefined;
  }
}
